import { zodResolver } from '@hookform/resolvers/zod';
import { useCreateTenant } from '@sunstone/api-client/hooks';
import {
  Alert,
  AlertDescription,
  Button,
  Card,
  CardContent,
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
} from '@sunstone/ui';
import { Loader2 } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { z } from 'zod';
import { PageHeader } from '@/components/ui/PageHeader';
import { useAuthToken } from '@/features/auth';
import { useAuthorization } from '@/features/auth/useAuthorization';
import { useSlugSync } from '@/hooks/useSlugSync';
import { routes } from '@/lib/routes';
import { displayNameSchema, slugSchema } from '@/lib/schemas';

const schema = z.object({
  name: displayNameSchema,
  slug: slugSchema,
  email: z.email('Enter a valid email address').min(1, 'Email is required'),
});

type FormValues = z.infer<typeof schema>;

export function NewPartnerPage() {
  const navigate = useNavigate();
  const { token } = useAuthToken();
  const { hasPermission } = useAuthorization();
  const createTenant = useCreateTenant(token);

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    mode: 'onChange',
    defaultValues: { name: '', slug: '', email: '' },
  });

  const syncSlug = useSlugSync(form);

  async function onSubmit(values: FormValues) {
    const result = await createTenant.mutateAsync({
      displayName: values.name.trim(),
      slug: values.slug,
    });
    const partnerId = result.tenant?.id;
    navigate(partnerId ? routes.tenant(partnerId) : routes.partners());
  }

  return (
    <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
      <PageHeader
        title="New Partner"
        description="Create a new partner organisation. Partners can have multiple customers."
      />

      <Card className="max-w-xl">
        <CardContent className="py-6">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5">
              {createTenant.error && (
                <Alert variant="destructive">
                  <AlertDescription>
                    {createTenant.error.message ?? 'Failed to create partner. Please try again.'}
                  </AlertDescription>
                </Alert>
              )}

              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      Partner name <span className="text-destructive">*</span>
                    </FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        placeholder="e.g. Acme Corporation"
                        autoFocus
                        onChange={(e) => syncSlug(e.target.value, field.onChange)}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="slug"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      URL Name <span className="text-destructive">*</span>
                    </FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="acme-corporation" />
                    </FormControl>
                    <FormDescription>Used in URLs and API references.</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      Primary contact email <span className="text-destructive">*</span>
                    </FormLabel>
                    <FormControl>
                      <Input {...field} type="email" placeholder="admin@acme.com" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="flex items-center justify-end gap-3 pt-2">
                <Button type="button" variant="ghost" size="sm" onClick={() => navigate(-1)}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  size="sm"
                  disabled={
                    !form.formState.isValid ||
                    createTenant.isPending ||
                    !hasPermission('create:partner')
                  }
                >
                  {createTenant.isPending ? (
                    <>
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      Creating…
                    </>
                  ) : (
                    'Create Partner'
                  )}
                </Button>
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>
    </div>
  );
}
